// Reference buffs. One per engine hook a buff can use: an ability grant, a
// spare life, an ability copy between pieces, a second royal, a capture
// hook, a two target relocation, a run along a file, a change of kind, and a pick that names a group. Each is a spec with
// no voice.

import {
  CAPTURE_AGAIN_SPELL_ID,
  CHANGE_KIND_WEIGHTS,
  CLONE_BUD_TURNS,
  PAWN_ADVANCE_BONUS,
  RELOCATED_STOP_TURNS,
  ROLL_DISCOUNT,
  ROLL_DISCOUNT_ROLLS,
  SACRIFICE_GROUP_MAX,
  SACRIFICE_GROUP_MIN,
  STOPPED_PENALTY_RATIO,
} from '../../constants';
import { CARDS } from '../../content/cards';
import { addAbilities, addLearnSources, BASE_ABILITIES, KIND_NAMES } from '../../engine/abilities';
import { cloneOnSquare } from '../../engine/apply';
import { ABILITY_VALUE, pieceValue } from '../../engine/evaluate';
import { createPiece, emptySquares, isSide, withPiece } from '../../engine/pieces';
import {
  fileOf,
  isOnOwnHalf,
  neighborSquares,
  promotionRank,
  rankOf,
  ranksAdvanced,
  squareName,
} from '../../engine/squares';
import { isDeadly, isSolid } from '../../engine/terrain';
import type { Piece, PieceKind, Side } from '../../engine/types';
import type { GameState } from '../../game/state';
import {
  BAIT_BONUS,
  canMoveNow,
  casterCanTake,
  castStopCost,
  comboLine,
  defineMechanic,
  describePiece,
  isFuseLit,
  isThreatened,
  lostMoveCost,
  noEffect,
  OWN_MOVE_PENALTY,
  OWNER_NAMES,
  ownMoveSquares,
  ownPieces,
  pieceAt,
  placeNewPiece,
  ROYAL_PICK_BONUS,
  relocatePiece,
  say,
  settleLanding,
  squaresWhere,
  stopMoved,
  TAKEN_SOON_PENALTY,
  UNSAFE_PENALTY,
  updatePiece,
  valueAt,
} from '../helpers';
import { grantAbility, markOwn, PICK_OWN } from '../kit';
import { isRunFatal, runEnd } from '../run';
import { sacrificeGroup } from '../sacrifice';
import { gainScroll, openScrollSlots } from '../scrolls';
import type { SpellMechanic } from '../types';
import { explodeLater } from './debuffs';

/** Hook: abilities. The move generator reads them, so any piece can learn any move. */
export const addKnightMoves = grantAbility(
  'add-knight-moves',
  'knight',
  'The piece gains knight moves.',
  {
    cast: '{who} gains {ability} moves.',
    castCombo: '{who} gains {ability} moves. It is a {combo} now.',
  },
);

/** Hook: lives. applyMove takes a life and bounces the attacker instead of capturing. */
export const extraLife = defineMechanic({
  id: 'extra-life',
  polarity: 'ally',
  family: 'status',
  targetCount: 1,
  aiPreference: 'royal',
  // A royal piece first, then the most valuable. The life protects a piece
  // the enemy can take, so the stop after the cast costs only the move.
  aiScore: (state, square, caster) =>
    (pieceAt(state, square).royal ? ROYAL_PICK_BONUS : 0) +
    valueAt(state, square) -
    lostMoveCost(state, square, caster),
  spec: {
    summary: 'The piece gains one extra life. A capture takes the life instead of the piece.',
    prompt: PICK_OWN,
    lines: { cast: '{who} has {lives} lives.' },
  },
  eligible: (state, caster) => ownPieces(state, caster),
  cast: ({ state, self }, [square]) => {
    if (square === undefined) return state;
    const piece = pieceAt(state, square);
    const next = updatePiece(state, square, { lives: piece.lives + 1 });
    return say(next, self, 'cast', { who: describePiece(piece, square), lives: piece.lives + 1 });
  },
});

const neighborsOfSameSide = (state: GameState, square: number, side: Side): number[] =>
  neighborSquares(square).filter((candidate) => state.board[candidate]?.owner === side);

/**
 * Seed from Garrett: abilities spread between neighbors.
 * Hook: abilities, read from another piece. A random friendly neighbor
 * learns every ability the picked piece has that it lacks.
 */
export const copyAbilities = defineMechanic({
  id: 'copy-abilities',
  polarity: 'ally',
  family: 'ability',
  targetCount: 1,
  // The pick is the source. The cast changes the neighbor, so the pick keeps its move.
  aiScore: (state, square) => valueAt(state, square),
  spec: {
    summary: 'A random friendly neighbor copies this piece’s abilities.',
    prompt: 'Pick the piece to copy. A random neighbor learns from it.',
    lines: {
      cast: '{who} copies {source}.',
      castCombo: '{who} copies {source}. It is a {combo} now.',
      noEffect: 'No neighbor had anything to learn.',
    },
  },
  eligible: (state, caster) =>
    ownPieces(state, caster).filter((square) =>
      neighborsOfSameSide(state, square, caster).some((neighbor) => {
        const source = pieceAt(state, square);
        const target = pieceAt(state, neighbor);
        return source.abilities.some((ability) => !target.abilities.includes(ability));
      }),
    ),
  cast: ({ state, caster, rng, self }, [square]) => {
    if (square === undefined) return state;
    const source = pieceAt(state, square);
    const candidates = neighborsOfSameSide(state, square, caster).filter((neighbor) =>
      source.abilities.some((ability) => !pieceAt(state, neighbor).abilities.includes(ability)),
    );
    if (candidates.length === 0) return noEffect(state, self);
    const neighbor = rng.pick(candidates);
    const target = pieceAt(state, neighbor);
    const learned = source.abilities.filter((ability) => !target.abilities.includes(ability));
    const next = updatePiece(state, neighbor, {
      abilities: addAbilities(target.abilities, learned),
      gained: addAbilities(target.gained, learned),
      learnedFrom: addLearnSources(target.learnedFrom, learned, { how: 'copy', kind: source.kind }),
    });
    const [key, vars] = comboLine(pieceAt(next, neighbor));
    return say(next, self, key, {
      who: describePiece(target, neighbor),
      source: describePiece(source, square),
      ...vars,
    });
  },
});

/**
 * Seed from Garrett: more than one royal piece.
 * Hook: royal. A side loses when it has no royal piece left, so a second
 * royal is a second thing the enemy must take.
 */
export const makeRoyal = markOwn({
  id: 'make-royal',
  family: 'royalty',
  summary: 'The piece becomes royal. The enemy must capture it too.',
  prompt: 'Pick the piece that becomes royal.',
  eligible: (piece) => !piece.royal,
  patch: { royal: true },
  lines: { cast: '{who} is royal. The enemy must take it too.' },
  aiPreference: 'cheap',
});

/** A cheap piece the enemy already wants to take. It stays, so the stop after the cast costs only the move. */
const baitScore: SpellMechanic['aiScore'] = (state, square, caster) =>
  -valueAt(state, square) +
  (isThreatened(state, square, caster) ? BAIT_BONUS : 0) -
  lostMoveCost(state, square, caster);

/** Hook: capture hooks in applyMove. This one removes the captor. See the trait table for the rest. */
export const killCaptor = markOwn({
  id: 'kill-captor',
  family: 'status',
  summary: 'Whoever captures this piece dies with it.',
  prompt: PICK_OWN,
  eligible: (piece) => !piece.killsCaptor && !piece.royal,
  patch: { killsCaptor: true },
  lines: { cast: '{who} is poisoned. Whoever takes it dies too.' },
  aiPreference: 'cheap',
  aiScore: baitScore,
});

/**
 * Seed from Garrett: a piece that pushes what it moves into.
 * Hook: a push in applyMove, before the capture rules. See engine/push.ts.
 * The caster may pick a piece of either side, a rook too. Rob, 2026-10-13: "Let's make push-pieces applicable
 * to any piece, ally or enemy (since it removes the capturing ability, in a
 * way)". A piece that pushes takes only a piece with no free square beside
 * it, so on an enemy piece the spell is a loss for the enemy. It was for a
 * piece of the caster only.
 */
export const pushPieces = defineMechanic({
  id: 'push-pieces',
  polarity: 'any',
  family: 'status',
  targetCount: 1,
  // The AI puts it on the enemy piece that is worth the most, which loses most of its
  // captures. It does not put it on a piece of its own.
  aiScore: (state, square, caster) =>
    state.board[square]?.owner === caster ? -valueAt(state, square) : valueAt(state, square),
  spec: {
    summary:
      'When the piece moves onto another piece, it pushes that piece to a free square beside it: straight on when that square is free, or the free square nearest to that. With no free square beside that piece, it captures as usual. The piece may be yours or the enemy’s.',
    prompt: 'Pick any piece, yours or the enemy’s.',
    lines: { cast: '{who} pushes the pieces it moves into.' },
  },
  eligible: (state) =>
    squaresWhere(
      state,
      (piece) => isSide(piece.owner) && !piece.pushes && piece.kind !== 'dormant',
    ),
  cast: ({ state, self }, [square]) => {
    if (square === undefined) return state;
    const piece = pieceAt(state, square);
    return say(updatePiece(state, square, { pushes: true }), self, 'cast', {
      who: describePiece(piece, square),
    });
  },
});

/**
 * The piece to relocate. The relocation stops the piece for the turn, so it
 * must pay for the lost move. A piece in danger pays: the relocation saves it.
 * A piece that cannot move now loses nothing. Any other piece loses its move,
 * so the cheapest one loses the least.
 */
const relocationGain = (state: GameState, square: number, caster: Side): number => {
  const value = valueAt(state, square);
  if (isThreatened(state, square, caster)) return value + BAIT_BONUS;
  const lostMove = value * STOPPED_PENALTY_RATIO;
  return canMoveNow(state, square) ? -lostMove : lostMove;
};

/**
 * The square to relocate to: safe first, then a clone square that makes a
 * copy, then far forward. The piece stays
 * there through the enemy turn, so the check runs on the board after the
 * relocation, where the piece no longer blocks or guards from its old square.
 */
const relocationSquareScore = (
  state: GameState,
  from: number,
  to: number,
  caster: Side,
): number => {
  const after = { ...state, board: relocatePiece(state.board, from, to) };
  const copied = cloneOnSquare(after.board, after.rules, to)?.spot != null;
  if (isDeadly(state.rules, to)) return -TAKEN_SOON_PENALTY;
  return (
    ranksAdvanced(to, caster) +
    (copied ? valueAt(after, to) : 0) -
    (ownMoveSquares(state, from).includes(to) ? OWN_MOVE_PENALTY : 0) -
    (isThreatened(after, to, caster) ? UNSAFE_PENALTY : 0)
  );
};

/** Hook: two targets, and relocation. The second pick is a square, not a piece. */
export const moveToOwnHalf = defineMechanic({
  id: 'move-to-own-half',
  polarity: 'ally',
  family: 'placement',
  targetCount: 2,
  spec: {
    summary:
      'Move this piece to any empty square on your half. It cannot move for the rest of the turn. A deadly square removes it.',
    prompt: 'Pick the piece, then pick an empty square on your half.',
    lines: { cast: '{who} moves to {square} and cannot move this turn.' },
  },
  aiScore: (state, square, caster, picked) => {
    const [from] = picked;
    return from === undefined
      ? relocationGain(state, square, caster)
      : relocationSquareScore(state, from, square, caster);
  },
  eligible: (state, caster, picked) =>
    picked.length === 0
      ? ownPieces(state, caster)
      : emptySquares(state.board).filter(
          (square) => isOnOwnHalf(square, caster) && !isSolid(state.rules, square),
        ),
  cast: ({ state, self }, [from, to]) => {
    if (from === undefined || to === undefined) return state;
    const piece = pieceAt(state, from);
    const moved = relocatePiece(state.board, from, to);
    const next = { ...state, board: stopMoved(moved, [to], RELOCATED_STOP_TURNS) };
    const said = say(next, self, 'cast', {
      who: describePiece(piece, from),
      square: squareName(to),
    });
    return settleLanding(said, to);
  },
});

/** The side of the piece on the square. A run goes forward for that side. */
const runnerOf = (state: GameState, square: number): Side => {
  const { owner } = pieceAt(state, square);
  if (!isSide(owner)) throw new Error(`No side owns the piece on ${squareName(square)}`);
  return owner;
};

/** The run on the board, for a score. */
const afterRun = (state: GameState, from: number, runner: Side): GameState => ({
  ...state,
  board: relocatePiece(state.board, from, runEnd(state, from, runner)),
});

/**
 * The ranks a run gives that the piece cannot get with its own move. A rook
 * on an open file gets none, and a pawn on its first rank gets none from a
 * run of two squares.
 */
const ranksBeyondOwnMove = (state: GameState, from: number, to: number, runner: Side): number => {
  const ownReach = ownMoveSquares(state, from)
    .filter((square) => fileOf(square) === fileOf(from))
    .reduce(
      (best, square) => Math.max(best, ranksAdvanced(square, runner)),
      ranksAdvanced(from, runner),
    );
  return ranksAdvanced(to, runner) - ownReach;
};

/**
 * The gain of a run of a piece of the caster: the ranks the piece cannot get
 * with its own move, and the value a pawn gets when it ends on the far rank.
 * A run the piece can make as its own move gains nothing, so the AI does not
 * pay for it. Garrett, 2026-10-12: "use eager beaver on a pwn and just move
 * the pawn 2 steps or on a rook which can already kinda do that, felt like
 * wasting an upgrade". A run that ends on an attacked square comes late, and
 * a run that removes the piece comes last.
 */
const ownRunGain = (state: GameState, from: number, caster: Side): number => {
  if (isRunFatal(state, from, caster)) return -TAKEN_SOON_PENALTY;
  const to = runEnd(state, from, caster);
  const after = afterRun(state, from, caster);
  const ranks = ranksBeyondOwnMove(state, from, to, caster);
  const gain = ranks > 0 ? ranks + valueAt(after, to) - valueAt(state, from) : 0;
  return gain - (isThreatened(after, to, caster) ? UNSAFE_PENALTY : 0);
};

/**
 * The gain of a run of an enemy piece: the whole piece when the run removes
 * it, or when it ends where the caster can take it and could not before. The
 * ranks the enemy piece goes forward, and a promotion, are a loss. The plan
 * of the cast runs the search, which sees whether the piece is guarded.
 */
const enemyRunGain = (state: GameState, from: number, caster: Side): number => {
  const runner = runnerOf(state, from);
  if (isRunFatal(state, from, runner)) return valueAt(state, from);
  const to = runEnd(state, from, runner);
  const after = afterRun(state, from, runner);
  const exposed = casterCanTake(after, to, caster) && !casterCanTake(state, from, caster);
  const promotion = valueAt(after, to) - valueAt(state, from);
  const ranks = ranksAdvanced(to, runner) - ranksAdvanced(from, runner);
  return (exposed ? valueAt(after, to) : 0) - promotion - ranks;
};

/**
 * Seed from Garrett, 2026-10-11: "lets a selected piece run straight ahead
 * until it encounters an enemy or end of board". The name on the card is his.
 * Hook: relocation along a line. The piece does not capture. It stops on the
 * last empty square before a piece of any side, a solid square, or a deadly
 * square. Garrett, the same day: "if a piece ends on a deadly square at the
 * top of the board it dies, but yeah otherwise it stops before them".
 * The caster may pick a piece of either side. Garrett, 2026-10-15: "eager
 * beaver should apply to any piece, can be offensive or defensive". The piece
 * runs toward the far rank of its own side, so an enemy piece runs at the
 * caster. It was for a piece of the caster only.
 */
export const runForward = defineMechanic({
  id: 'run-forward',
  polarity: 'any',
  family: 'placement',
  targetCount: 1,
  spec: {
    summary:
      'The piece goes straight ahead, toward the far side of its owner, until the next square holds a piece or the board ends. It does not capture. It stops before a deadly square, but a deadly square on the far rank removes it. It cannot move for the rest of the turn. The piece may be yours or the enemy’s.',
    prompt: 'Pick any piece, yours or the enemy’s, with an empty square ahead.',
    lines: {
      cast: '{who} runs to {square} and cannot move this turn.',
      castGone: '{who} runs into the deadly square on {square}. Gone.',
    },
  },
  aiScore: (state, square, caster) =>
    runnerOf(state, square) === caster
      ? ownRunGain(state, square, caster)
      : enemyRunGain(state, square, caster),
  eligible: (state) =>
    squaresWhere(
      state,
      (piece, square) => isSide(piece.owner) && runEnd(state, square, piece.owner) !== square,
    ),
  cast: ({ state, self }, [from]) => {
    if (from === undefined) return state;
    const piece = pieceAt(state, from);
    const runner = runnerOf(state, from);
    const to = runEnd(state, from, runner);
    const vars = { who: describePiece(piece, from), square: squareName(to) };
    if (isRunFatal(state, from, runner)) {
      const gone = {
        ...state,
        board: withPiece(state.board, from, null),
        captured: [...state.captured, piece],
      };
      return say(gone, self, 'castGone', vars);
    }
    const moved = relocatePiece(state.board, from, to);
    const next = { ...state, board: stopMoved(moved, [to], RELOCATED_STOP_TURNS) };
    return settleLanding(say(next, self, 'cast', vars), to);
  },
});

/** The kinds a piece can change into. A blocker and a dormant piece are not in the draw. */
const CHANGE_KINDS: readonly PieceKind[] = ['pawn', 'knight', 'bishop', 'rook', 'queen', 'king'];

/**
 * Every kind but the piece's own. A pawn on its far rank could never move,
 * and promotion would change it again, so that square leaves the pawn out.
 */
const kindsToChangeInto = (piece: Piece, square: number): PieceKind[] =>
  CHANGE_KINDS.filter(
    (kind) =>
      kind !== piece.kind &&
      !(kind === 'pawn' && isSide(piece.owner) && rankOf(square) === promotionRank(piece.owner)),
  );

const changeWeight = (kind: PieceKind): number => CHANGE_KIND_WEIGHTS[kind] ?? 0;

/** One kind from the list, by `CHANGE_KIND_WEIGHTS`. It takes one number from the source. */
const drawKind = (kinds: readonly PieceKind[], roll: number): PieceKind => {
  const total = kinds.reduce((sum, kind) => sum + changeWeight(kind), 0);
  let left = roll * total;
  for (const kind of kinds) {
    left -= changeWeight(kind);
    if (left < 0) return kind;
  }
  const last = kinds.at(-1);
  if (last === undefined) throw new Error('draw from an empty list');
  return last;
};

/** The value the draw gives on average, with each kind at its weight. */
const expectedKindValue = (kinds: readonly PieceKind[]): number => {
  const total = kinds.reduce((sum, kind) => sum + changeWeight(kind), 0);
  return kinds.reduce((sum, kind) => sum + kindValue(kind) * changeWeight(kind), 0) / total;
};

/**
 * The piece as another kind. It keeps the abilities a spell added, its
 * traits, and its lives. A king is royal. A royal piece stays royal, so a
 * king that changes does not end the match.
 */
const withKind = (piece: Piece, kind: PieceKind): Piece => {
  const gained = piece.gained.filter((ability) => !BASE_ABILITIES[kind].includes(ability));
  return {
    ...piece,
    kind,
    abilities: addAbilities(BASE_ABILITIES[kind], gained),
    gained,
    royal: piece.royal || kind === 'king',
  };
};

const kindValue = (kind: PieceKind): number =>
  BASE_ABILITIES[kind].reduce((sum, ability) => sum + ABILITY_VALUE[ability], 0);

/**
 * Hook: kind. The silhouette and the base abilities change together. A piece
 * of the caster only. Changed 2026-10-14: on an enemy piece the AI used it to
 * take the queen away, 201 of 331 enemy casts in a study of 225 matches.
 */
export const changeKind = defineMechanic({
  id: 'change-kind',
  polarity: 'ally',
  family: 'royalty',
  targetCount: 1,
  spec: {
    summary:
      'One of your pieces becomes a random other kind. A piece that becomes a king is royal.',
    prompt: 'Pick one of your pieces.',
    lines: {
      cast: '{who} is a {kind} now.',
      castRoyal: '{who} is a {kind} now. It is royal.',
    },
  },
  // The average gain in base value, so a cheap piece comes first. The piece cannot move
  // this turn, so one the enemy can take comes late.
  aiScore: (state, square, caster) => {
    const piece = pieceAt(state, square);
    const gain = expectedKindValue(kindsToChangeInto(piece, square)) - kindValue(piece.kind);
    return gain - castStopCost(state, square, caster);
  },
  eligible: (state, caster) =>
    ownPieces(state, caster, (piece) => CHANGE_KINDS.includes(piece.kind)),
  cast: ({ state, rng, self }, [square]) => {
    if (square === undefined) return state;
    const piece = pieceAt(state, square);
    const changed = withKind(piece, drawKind(kindsToChangeInto(piece, square), rng.next()));
    const next = updatePiece(state, square, changed);
    const key = changed.royal && !piece.royal ? 'castRoyal' : 'cast';
    return say(next, self, key, {
      who: describePiece(piece, square),
      kind: KIND_NAMES[changed.kind],
    });
  },
});

/** What a sacrifice gives: one draw from the first list for two pawns, from the second for three. */
const KINDS_FOR_TWO_PAWNS: readonly PieceKind[] = ['rook', 'bishop', 'knight'];
const KINDS_FOR_THREE_PAWNS: readonly PieceKind[] = ['queen', 'king'];

const kindsForSacrifice = (pawnCount: number): readonly PieceKind[] =>
  pawnCount >= SACRIFICE_GROUP_MAX ? KINDS_FOR_THREE_PAWNS : KINDS_FOR_TWO_PAWNS;

const canSacrificeFrom = (state: GameState, square: number): boolean =>
  sacrificeGroup(state.board, square).length >= SACRIFICE_GROUP_MIN;

/** "d2 and e2", or "d2, e2, and f2". */
const listSquares = (squares: readonly number[]): string => {
  const names = squares.map(squareName);
  const last = names.pop();
  if (names.length === 0) return last ?? '';
  return `${names.join(', ')}${names.length > 1 ? ',' : ''} and ${last}`;
};

/**
 * Seed from Garrett: pawns that touch are removed for one stronger piece.
 * Hook: a pick that names a group. `removes` gives the group to the board, so
 * the player sees every pawn that goes before the click.
 */
export const sacrificePawns = defineMechanic({
  id: 'sacrifice-pawns',
  polarity: 'ally',
  family: 'royalty',
  targetCount: 1,
  spec: {
    summary:
      'Remove two of your pawns that touch, and get a random rook, bishop, or knight. Remove three that connect, and get a random queen or king. The new piece comes on one of the emptied squares.',
    // The cue headline has two lines. The crosses on the board show the rest of the group.
    prompt: 'Pick a pawn to remove.',
    lines: {
      cast: '{owner} removed the pawns on {pawns}. A {kind} is on {square}.',
      castRoyal: '{owner} removed the pawns on {pawns}. A {kind} is on {square}. It is royal.',
    },
  },
  // The average value of the draw, less the pawns it costs. A pawn far forward costs more.
  aiScore: (state, square, caster) => {
    const group = sacrificeGroup(state.board, square);
    const kinds = kindsForSacrifice(group.length);
    const gain =
      kinds.reduce((sum, kind) => sum + pieceValue(createPiece(0, caster, kind, square)), 0) /
      kinds.length;
    const cost = group.reduce(
      (sum, pawn) => sum + valueAt(state, pawn) + ranksAdvanced(pawn, caster) * PAWN_ADVANCE_BONUS,
      0,
    );
    return gain - cost;
  },
  eligible: (state, caster) =>
    ownPieces(state, caster).filter((square) => canSacrificeFrom(state, square)),
  removes: (state, _caster, square) => sacrificeGroup(state.board, square),
  cast: ({ state, caster, rng, self }, [square]) => {
    if (square === undefined) return state;
    const group = sacrificeGroup(state.board, square);
    if (group.length < SACRIFICE_GROUP_MIN) return state;
    const pawns = group.map((pawn) => pieceAt(state, pawn));
    const kind = rng.pick(kindsForSacrifice(group.length));
    const landing = rng.pick(group);
    const emptied = group.reduce((board, pawn) => withPiece(board, pawn, null), state.board);
    const next = placeNewPiece(
      { ...state, board: emptied, captured: [...state.captured, ...pawns] },
      landing,
      caster,
      kind,
    );
    return say(next, self, kind === 'king' ? 'castRoyal' : 'cast', {
      owner: OWNER_NAMES[caster],
      pawns: listSquares([...group].sort((a, b) => a - b)),
      kind: KIND_NAMES[kind],
      square: squareName(landing),
    });
  },
});

/**
 * Seed from Garrett: a card that removes the timer of explode-later.
 * Hook: answers. The wheel puts this face in place of explode-later after a
 * roll lights a fuse, and puts explode-later back when no fuse is lit.
 */
export const defuseBomb: SpellMechanic = {
  ...markOwn({
    id: 'defuse-bomb',
    family: 'timer',
    summary: 'One of your pieces that is about to explode does not explode.',
    prompt: 'Pick one of your pieces that is about to explode.',
    eligible: (piece) => piece.explodeIn > 0,
    patch: { explodeIn: 0 },
    lines: { cast: '{who} does not explode.' },
    // The blast the caster loses the most from: the piece and its neighbors
    // of the same side, less the enemy pieces the blast would take.
    aiScore: (state, square, caster) =>
      neighborSquares(square).reduce(
        (sum, neighbor) => {
          const owner = state.board[neighbor]?.owner;
          if (owner === undefined || owner === 'neutral') return sum;
          return sum + (owner === caster ? 1 : -1) * valueAt(state, neighbor);
        },
        valueAt(state, square),
      ),
  }),
  answers: explodeLater.id,
  canDeal: (_wheel, board) => isFuseLit(board),
};

/**
 * Seed from Rob: "next time this piece captures, it moves again". It was
 * "Next turn, if this piece captures, it can move again" until 2026-10-12,
 * with a timer.
 * Hook: a flag, read after a move in flow.ts. A capture gives one more move
 * and clears the flag. Rob: "Only one extra move. No chains." The piece must
 * make the move, as with movesTwice. When it has no move, the turn ends.
 */
export const checkersEnergy = markOwn({
  id: CAPTURE_AGAIN_SPELL_ID,
  family: 'status',
  summary: 'The next time this piece captures, it moves again, once.',
  prompt: PICK_OWN,
  eligible: (piece) => !piece.capturesAgain && piece.kind !== 'dormant',
  patch: { capturesAgain: true },
  lines: { cast: '{who} moves again the next time it captures.' },
});

/**
 * Seed from Rob: "Clones this piece onto an adjacent square in 3 turns".
 * Hook: a timer that fires. flow.ts puts a copy on a random empty square
 * beside the piece at the start of the turn the timer ends on. With no empty
 * square there is no copy. Rob, 2026-10-12: "the clone fizzles if the piece
 * is surrounded. That's a valid defense against cloning."
 */
export const cloneBud = markOwn({
  id: 'clone-bud',
  family: 'timer',
  summary: `In ${CLONE_BUD_TURNS} turns, a copy of this piece comes on a random empty square beside it. With no empty square beside it, no copy comes.`,
  prompt: PICK_OWN,
  eligible: (piece) => piece.cloneIn === 0 && piece.kind !== 'dormant',
  patch: { cloneIn: CLONE_BUD_TURNS },
  vars: { turns: CLONE_BUD_TURNS },
  lines: { cast: 'A copy of {who} comes in {turns} turns.' },
});

/**
 * Seed from Rob: "Suggests the highest-value move on the board".
 * Hook: a flag on the state for the turn. The cast changes no piece. For the
 * player, `state/suggestion.ts` runs the search with a long budget and the
 * board marks the move. For the AI, the driver gives its own search the same
 * budget, so it looks deeper than on another turn.
 */
export const suggestBest = defineMechanic({
  id: 'suggest-best',
  polarity: 'ally',
  family: 'status',
  spec: {
    summary: 'The search shows you its best move for this turn. It looks deeper than usual.',
    prompt: '',
    lines: { cast: '{owner} gets a suggestion for this move.' },
  },
  cast: ({ state, caster, self }) =>
    say({ ...state, suggestFor: caster }, self, 'cast', { owner: OWNER_NAMES[caster] }),
});

/**
 * Seed from Rob: "Your next two rolls cost one less energy".
 * Hook: the cost of a roll. `rollCost` reads the count, and a roll the side
 * pays for uses one. Rob, 2026-10-12: "a roll can cost 0".
 */
export const rollDiscount = defineMechanic({
  id: 'roll-discount',
  polarity: 'ally',
  family: 'timer',
  spec: {
    summary: `Your next ${ROLL_DISCOUNT_ROLLS} casts cost ${ROLL_DISCOUNT} less energy each.`,
    prompt: '',
    lines: { cast: 'The next {rolls} casts of {owner} cost {discount} less energy each.' },
  },
  cast: ({ state, caster, self }) => {
    const rollDiscounts = {
      ...state.rollDiscounts,
      [caster]: state.rollDiscounts[caster] + ROLL_DISCOUNT_ROLLS,
    };
    return say({ ...state, rollDiscounts }, self, 'cast', {
      owner: OWNER_NAMES[caster],
      rolls: ROLL_DISCOUNT_ROLLS,
      discount: ROLL_DISCOUNT,
    });
  },
});

/**
 * Seed from Rob: "Add a random scroll into an open slot".
 * Hook: the scroll slots of a caster. It is cast only with an open slot, so
 * the wheel never deals it: a match with a wheel has no slots.
 */
export const newScroll = defineMechanic({
  id: 'new-scroll',
  polarity: 'ally',
  family: 'status',
  spec: {
    summary: 'A random scroll goes into one of your open scroll slots.',
    prompt: '',
    lines: { cast: '{owner} gets a scroll: {scroll}.', noEffect: 'No scroll slot is open.' },
  },
  canDeal: () => false,
  canCast: (state, caster) => openScrollSlots(state, caster) > 0,
  cast: ({ state, caster, rng, self }) => {
    const gained = gainScroll(state, caster, rng);
    if (gained.scroll === null) return noEffect(state, self);
    return say(gained.state, self, 'cast', {
      owner: OWNER_NAMES[caster],
      scroll: CARDS[gained.scroll]?.name || gained.scroll.replace(/-/g, ' '),
    });
  },
});

export const BUFFS: readonly SpellMechanic[] = [
  addKnightMoves,
  extraLife,
  copyAbilities,
  makeRoyal,
  killCaptor,
  pushPieces,
  moveToOwnHalf,
  runForward,
  changeKind,
  sacrificePawns,
  defuseBomb,
  rollDiscount,
  checkersEnergy,
  cloneBud,
  suggestBest,
  newScroll,
];
